import { create } from 'zustand'

import { cookieSource, type SessionSource } from '@/lib/auth/session-source'

/**
 * Why a session ended. `member`: they signed out. `expired`: it could not be
 * renewed, so they never chose to leave, and signing in again should put them
 * back where they were. `rejected`: it was renewed, and mm-be refused even the
 * fresh token — expired as far as the member is concerned, kept apart because
 * the Flutter app is told which one it was (lib/embed).
 */
export type SignOutReason = 'member' | 'expired' | 'rejected'

interface AuthState {
  /**
   * Held in memory, and kept for a reload where the source allows it (a
   * browser tab mirrors it into sessionStorage, session.ts). There is no
   * refresh token here: mm-be keeps it in an HttpOnly cookie, or the Flutter
   * app keeps it.
   */
  accessToken: string | null
  /** 'unknown' until `ensureSession` has looked for a session. */
  status: 'unknown' | 'authenticated' | 'anonymous'
  /** Why the last session ended; null while one is live, or before any has. */
  signedOutBy: SignOutReason | null

  signIn: (tokens: { access: string }) => void
  /**
   * A fresh token for the session already live, handed over unasked (the
   * Flutter app renewed early). Starts a session when none is live.
   */
  swapToken: (access: string) => void
  /** Ends the session in this tab, with no request. */
  signOut: (reason?: SignOutReason) => void
  /** The member's own sign-out: the source ends it (mm-be clears the cookie), then `signOut`. */
  logOut: () => Promise<void>
  /** Resolves once the tab knows whether it has a session. True if it does. */
  ensureSession: () => Promise<boolean>
  refresh: () => Promise<boolean>
}

/**
 * Where tokens come from. Picked once, before the first `ensureSession`, by
 * `setSessionSource`; a browser tab never changes it.
 */
let source: SessionSource = cookieSource

/**
 * Hands the store its token source. Boot only (lib/embed calls it in the
 * Flutter app's WebView): swapped mid-session, a token kept by one source
 * would be renewed by the other.
 */
export const setSessionSource = (next: SessionSource): void => {
  source = next
}

/**
 * Deduplicates concurrent refreshes — tokens found expired before sending,
 * 401s, and the boot check — so many callers spend one refresh call.
 */
let inflightRefresh: Promise<boolean> | null = null

/**
 * Bumped whenever a session starts or ends. A refresh notes it on the way out
 * and acts on its answer only if it is unchanged: otherwise someone signed out
 * (or in) while it was in flight, and its answer belongs to a session that is
 * gone. A counter, because the refresh token it used to compare is a cookie
 * this code cannot read.
 */
let sessionEpoch = 0

export const useAuthStore = create<AuthState>((set, get) => {
  /** A refresh that failed ends the session it was renewing, if there was one. */
  const endUnrenewedSession = () => {
    // A tab still booting had no session to lose: it is simply signed out,
    // which is not a sign-out (see app/session-sync.ts).
    if (get().status === 'unknown') {
      set({ accessToken: null, status: 'anonymous', signedOutBy: null })
    } else {
      get().signOut('expired')
    }
  }

  return {
    accessToken: null,
    status: 'unknown',
    signedOutBy: null,

    signIn: ({ access }) => {
      sessionEpoch += 1
      source.keep(access)
      set({ accessToken: access, status: 'authenticated', signedOutBy: null })
    },

    swapToken: (access) => {
      if (get().status !== 'authenticated') {
        get().signIn({ access })
        return
      }
      // The same session, so no epoch bump: a refresh in flight for it is
      // still renewing it, and dropping its answer would fail the request
      // that is waiting on it.
      source.keep(access)
      set({ accessToken: access })
    },

    signOut: (reason = 'member') => {
      sessionEpoch += 1
      source.forget()
      set({ accessToken: null, status: 'anonymous', signedOutBy: reason })
    },

    logOut: async () => {
      await source.endSession()
      get().signOut('member')
    },

    ensureSession: async () => {
      if (get().status === 'unknown') {
        // A reload: this tab's own access token, with no round trip. One that
        // has expired since is renewed by the api client before it is sent.
        const stored = source.restore()
        if (stored !== null)
          set({ accessToken: stored, status: 'authenticated', signedOutBy: null })
      }
      // A new tab, or a browser restarted: one refresh decides. Its failure
      // leaves 'anonymous', so it is never tried twice in a tab.
      if (get().status === 'unknown') await get().refresh()
      return get().status === 'authenticated'
    },

    refresh: async () => {
      if (inflightRefresh) return inflightRefresh

      const epoch = sessionEpoch
      const reason = get().status === 'unknown' ? 'boot' : 'expired'
      inflightRefresh = (async () => {
        try {
          const renewal = await source.renew(reason)

          // Someone signed out (or in) while this was in flight. Reviving or
          // ending the session on its word would undo what they did.
          if (sessionEpoch !== epoch) return false

          if ('access' in renewal) {
            get().signIn({ access: renewal.access })
            return true
          }
          // "Not now" is not "never": a live session keeps its spent token,
          // the request that asked fails, and the next one asks again. A tab
          // still booting has no session to keep, so it settles as signed out.
          if (renewal.error === 'unavailable' && get().status === 'authenticated') return false
          endUnrenewedSession()
          return false
        } finally {
          inflightRefresh = null
        }
      })()

      return inflightRefresh
    },
  }
})

/** Non-reactive read, for the api client middleware (outside React). */
export const getAccessToken = (): string | null => useAuthStore.getState().accessToken
export const refreshAccessToken = (): Promise<boolean> => useAuthStore.getState().refresh()
/**
 * The api client's: it ends a session the server rejected even after a
 * refresh, so it counts as `rejected`. It asks nothing of mm-be: the session
 * renews into tokens mm-be refuses, and asking again would loop. A member's
 * own sign-out goes through the store's `logOut`.
 */
export const signOut = (): void => {
  useAuthStore.getState().signOut('rejected')
}
